import * as React from 'react';
import { useEffect, useState } from 'react';
import { Accordion, Card, FormGroup } from 'react-bootstrap';
import RadioButton from 'components/buttons/RadioButton';
import BlazeInputDropdown, { BlazeDropdownItem } from 'components/ui/dropdown/BlazeDropdown/BlazeInputDropdown';
import { DataModel, Entity } from '@blazemeter/blaze-data-model';
import _ from 'lodash';
import { MeModel } from '@blazemeter/blaze-data-model/src/model/DataModel';
import { IterationOptions } from '@blazemeter/blaze-data-designer-client';
import { DataPublishConfig, IterationOverrides } from 'types/rest/blazeDataTypes';
import { MockService } from 'types/rest/mockServiceTypes';
import { useIsMount } from 'hooks/useIsMountHook';
import {
  createDsConfig,
  createPublishConfig,
  getEntityFromModel,
  getFallbackPublishConfig,
  getIterationOverrides,
  getPublishConfigFromTdmEntity,
} from 'utils/blazeDataUtils';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import i18n from 'i18n';
import IterationCount from '../IterationCount';
import MixpanelService from 'utils/mixpanelService';
import { DATA_SETTINGS_CREATE_ITERATION_OVERRIDE } from 'utils/mixpanelEvents';
import { isFindAndReserveIntegrationFeatureEnabled } from 'utils/bzmUserConfigUtil';
import AccordionItem from '../Accordion/AccordionItem';
import SqlBootstrapForm from '../../MockServiceDetail/SqlBootstrapTab';

export const GLOBAL_SCOPE_KEY = '__global_scope';
export const SQL_BOOTSTRAP_SCRIPT = '__sql_bootstrap_script';

export interface BlazeDataEntitiesAccordionProps {
  /** Virtual Service / draft holding iteration settings */
  mockService: MockService | MockServiceTemplate;

  /** Data Model from TDM backend, entities within this model are mapped to list of accordion groups */
  dataModel: DataModel | null;

  /** Existing publish configuration for entities */
  iterationOverrides?: IterationOverrides | null;

  /** Triggered when publish configuration changes*/
  onUpdateIterationOverrides: (config: IterationOverrides) => void;

  /** Id of currently selected entity from the model */
  activeEntity?: string;

  /** When entity is selected */
  onActiveEntityChanged: (entityId: string | undefined) => void;

  disabled: boolean;

  /** When true, hides the SQL Bootstrap Script accordion item */
  hideSqlBootstrap?: boolean;

  /** When provided, overrides the displayed repeat count for all entities (sandbox use only). */
  overrideRepeatCount?: number;
}

const BlazeDataEntitiesAccordion = (props: BlazeDataEntitiesAccordionProps) => {
  /**
   * Builds local config map based on the entities in the model,
   * We should do merge of the default config with existing one stored in virtual service
   */
  const buildPublishConfig = () => {
    const defaultConfigMap: DataPublishConfig[] = [];
    if (_.isNil(props.dataModel)) {
      return defaultConfigMap;
    }

    _.map((props.dataModel as MeModel).entities, (entity, key) => {
      //Fallback publish config when there is no configuration available
      let publishConfig = getFallbackPublishConfig(key);

      //Get publish config for this entity from virtual service
      const msPublishConfig = _.cloneDeep(props.mockService.dataSettings);
      if (
        msPublishConfig &&
        _.has(msPublishConfig, 'data.attributes.iterationOverride') &&
        !_.isNil(msPublishConfig.data.attributes.iterationOverride.entities[key])
      ) {
        const entityIterationConfig = msPublishConfig.data.attributes.iterationOverride.entities[key];
        const publishConfigFromMs = createPublishConfig(key, entityIterationConfig);
        if (publishConfigFromMs) {
          publishConfig = publishConfigFromMs;
        }
      }
      //if it does not exist, try to read iteration settings from the blaze data model
      else {
        const entity = getEntityFromModel(key, props.dataModel);
        const tdmIterationConfig = getPublishConfigFromTdmEntity(entity);
        const publishConfigFromTdm = createPublishConfig(key, tdmIterationConfig);
        if (publishConfigFromTdm) {
          publishConfig = publishConfigFromTdm;
        }
      }

      defaultConfigMap.push(publishConfig);
    });

    return defaultConfigMap;
  };

  /**
   * Updates single value in the list of publish configurations.
   * @param key - entity key
   * @param attrName - name of the attribute
   * @param attrValue - attribute value
   */
  const updatePublishConfig = (key: string, attrName: string, attrValue: any) => {
    const index = publishConfig.findIndex(item => item.entityKey === key);

    //Trigger the update only if value has changed
    if (index !== -1 && publishConfig[index][attrName] !== attrValue) {
      const updatedConfig = _.cloneDeep(publishConfig);
      updatedConfig[index][attrName] = attrValue;
      setPublishConfig(updatedConfig);
    }
  };

  const updatePublishConfigDebounced = _.debounce(updatePublishConfig, 600);

  const [publishConfig, setPublishConfig] = useState(() => buildPublishConfig());
  const isMount = useIsMount();

  /**
   * When publish config changes, it is propagated to the store & backend.
   */
  useEffect(() => {
    if (!isMount) {
      propagateIterationOverrides();
    }
  }, [publishConfig]);

  const getPublishConfig = (key: string, attrName: string) => {
    const index = publishConfig.findIndex(item => item.entityKey === key);
    if (index !== -1) {
      return _.get(publishConfig[index], attrName);
    }
    return null;
  };

  /**
   * Change of the currently selected data model. If it is the same, we set it to undefined so accordion
   * gets collapsed.
   * */
  const activeDataModelChangedHandler = (id: string) => {
    if (props.activeEntity !== id) {
      props.onActiveEntityChanged(id);
    }
  };

  /**
   *  Go through all data sources within selected entity and crate override for each data source option,
   *  set loop based on the data publish configuration (max, min)
   * */
  const getDataSourceOverrides = (config: DataPublishConfig) => {
    try {
      const entity: Entity | null = getEntityFromModel(config.entityKey, props.dataModel);
      return createDsConfig(config, entity);
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Propagate up transformed publish config
   */
  const propagateIterationOverrides = () => {
    props.onUpdateIterationOverrides(getIterationOverrides(publishConfig, getDataSourceOverrides));
    MixpanelService.track(DATA_SETTINGS_CREATE_ITERATION_OVERRIDE());
  };

  /**
   * Map list of resources in entity to BlazeDropdownItem.
   */
  const buildDataSourceDropdownItems = (entity: Entity) => {
    if (!_.isNil(entity.datasources) && !_.isEmpty(entity.datasources)) {
      const dataSources: BlazeDropdownItem[] = entity.datasources.map(dataSource => {
        return {
          name: dataSource.displayName ? dataSource.displayName : dataSource.name,
          id: dataSource.name,
          metaData: dataSource,
        } as BlazeDropdownItem;
      });
      return dataSources;
    }
    return [];
  };

  const getSelectedDataSource = (dataSources: BlazeDropdownItem[], entity: any, key: any) => {
    const selectedValue = getPublishConfig(key, 'resourceName');
    if (!_.isNil(selectedValue)) {
      //Find selected item in list of data sources from TDM
      const selectedDropdownItem: BlazeDropdownItem | undefined = _.find(
        dataSources,
        item => item.name === selectedValue,
      );
      return selectedDropdownItem ? selectedDropdownItem : null;
    }

    //If there is not a saved value in the config, if not, select first item
    if (dataSources.length !== 0) {
      return dataSources[0];
    }

    return null;
  };

  const selectDataSourceHandler = (key: string) => {
    const index = publishConfig.findIndex(item => item.entityKey === key);
    if (index === -1) {
      return;
    }

    const updatedConfig = _.cloneDeep(publishConfig);
    updatedConfig[index].iterationOptions = IterationOptions.ALL_FROM_DS;

    //If there is empty source, preselect first one
    const resourceName = getPublishConfig(key, 'resourceName');
    if (_.isEmpty(resourceName)) {
      const entity: Entity | null = getEntityFromModel(key, props.dataModel);
      if (entity && entity.datasources) {
        updatedConfig[index].resourceName = entity.datasources[0].name;
      }
    }
    setPublishConfig(updatedConfig);
  };

  const renderGlobalStateItem = () => {
    return (
      <AccordionItem
        key={GLOBAL_SCOPE_KEY}
        title={'Global Scope'}
        onClick={activeDataModelChangedHandler}
        isActive={props.activeEntity === GLOBAL_SCOPE_KEY}
        eventKey={GLOBAL_SCOPE_KEY}
      >
        <p className="p-4 mb-0 text-muted">{i18n.t('dataSettings:dataModel.globalScope')}</p>
      </AccordionItem>
    );
  };

  const renderSqlBoostrapScript = () => {
    return (
      <AccordionItem
        key={SQL_BOOTSTRAP_SCRIPT}
        title={i18n.t('transactions:editor.blazeDataCacheSettings')}
        onClick={activeDataModelChangedHandler}
        isActive={props.activeEntity === SQL_BOOTSTRAP_SCRIPT}
        eventKey={SQL_BOOTSTRAP_SCRIPT}
      >
        <p className="p-4 mb-0 text-muted">Define sql booststrap script</p>
      </AccordionItem>
    );
  };

  /**
   * Renders data entities in Accordeon component.
   * */
  const renderEntities = () => {
    if (!_.has(props.dataModel, 'entities')) {
      return;
    }
    return _.map((props.dataModel as MeModel).entities, (entity, key) => {
      const isActive = props.activeEntity === key;
      const dataSources = buildDataSourceDropdownItems(entity);
      const repeatCount = props.overrideRepeatCount ?? getPublishConfig(key, 'repeat');
      const frModelTitle = isFindAndReserveIntegrationFeatureEnabled()
        ? i18n.t('dataSettings:dataModel.orFrModel')
        : '';

      const content = (
        <Card.Body>
          <label>{i18n.t('dataSettings:dataModel.dataSizeOptions')}</label>
          {/*number of Rows*/}
          <FormGroup>
            <RadioButton
              disabled={props.disabled}
              id={'numberRows'}
              isChecked={getPublishConfig(key, 'iterationOptions') === IterationOptions.DEFINED}
              label={i18n.t('dataSettings:dataModel.numberOfRows')}
              onChange={() => updatePublishConfig(key, 'iterationOptions', IterationOptions.DEFINED)}
            ></RadioButton>
            <div className="ml-5">
              <IterationCount
                value={repeatCount ? repeatCount : 0}
                onChange={value => updatePublishConfigDebounced(key, 'repeat', parseInt(value))}
                disabled={props.disabled || getPublishConfig(key, 'iterationOptions') !== IterationOptions.DEFINED}
              ></IterationCount>
            </div>
          </FormGroup>
          {/* ./ number of Rows */}

          {/* All From Data Source */}
          <FormGroup>
            <RadioButton
              id={'rowsFromModel'}
              disabled={props.disabled || dataSources.length === 0}
              isChecked={getPublishConfig(key, 'iterationOptions') === IterationOptions.ALL_FROM_DS}
              label={i18n.t('dataSettings:dataModel.allRows', { orFrModel: frModelTitle })}
              onChange={() => selectDataSourceHandler(key)}
            ></RadioButton>

            <div className="ml-5">
              <BlazeInputDropdown
                id={'data-size-picker' + key}
                disabled={
                  props.disabled ||
                  dataSources.length === 0 ||
                  getPublishConfig(key, 'iterationOptions') !== IterationOptions.ALL_FROM_DS
                }
                showGroups={false}
                isSearchable={false}
                minDropdownMenuWidth={290}
                maxDropdownHeight={250}
                items={dataSources}
                selected={getSelectedDataSource(dataSources, entity, key)}
                onClick={dataSource => updatePublishConfig(key, 'resourceName', dataSource.name)}
                isDefaultOptionSelectable={true}
              ></BlazeInputDropdown>
            </div>
          </FormGroup>
          {/* ./All From Data Source */}

          {/* Use Longest Mode */}
          <RadioButton
            disabled={props.disabled || dataSources.length <= 1}
            id={'useLongestMode'}
            isChecked={getPublishConfig(key, 'iterationOptions') === IterationOptions.MAX_ROWS}
            label={i18n.t('dataSettings:dataModel.longestCsv', { orFrModel: frModelTitle })}
            onChange={() => updatePublishConfig(key, 'iterationOptions', IterationOptions.MAX_ROWS)}
          />
          {/* ./ Use Longest Mode */}

          {/* Use Shortest Mode */}
          <RadioButton
            disabled={props.disabled || dataSources.length <= 1}
            id={'useShortestMode'}
            isChecked={getPublishConfig(key, 'iterationOptions') === IterationOptions.MIN_ROWS}
            label={i18n.t('dataSettings:dataModel.shortestCsv', { orFrModel: frModelTitle })}
            onChange={() => updatePublishConfig(key, 'iterationOptions', IterationOptions.MIN_ROWS)}
          />
          {/* ./ Use Shortest Mode */}
        </Card.Body>
      );

      return (
        <AccordionItem
          key={key}
          title={entity.title}
          onClick={activeDataModelChangedHandler}
          isActive={isActive}
          eventKey={key}
        >
          {content}
        </AccordionItem>
      );
    });
  };

  return (
    <>
      <h4 className="mb-4 font-weight-semi-bold">{i18n.t('dataSettings:dataModel.title')}</h4>
      <Accordion activeKey={props.activeEntity}>
        {renderEntities()}
        {renderGlobalStateItem()}
        {!props.hideSqlBootstrap && renderSqlBoostrapScript()}
      </Accordion>
    </>
  );
};

export default BlazeDataEntitiesAccordion;
